<template>
    <div class="flex flex-col items-center max-w-4xl mx-auto">
        <div v-if="onboardingDraft.products.length" class="w-full">
            <h3 class="font-semibold text-theme-base mb-3">
                Productos creados ({{ onboardingDraft.products.length }})
            </h3>
            <div class="space-y-2">
                <div
                    v-for="product in onboardingDraft.products"
                    :key="product.id"
                    class="bg-theme-card border border-theme-border rounded-xl p-4 flex items-center justify-between"
                >
                    <div>
                        <p class="font-medium text-theme-base">
                            {{ product.name }}
                        </p>
                        <p class="text-sm text-theme-muted">
                            ${{ product.references[0].price.toLocaleString() }}
                        </p>
                    </div>
                    <span class="text-theme-success text-sm font-medium">
                        Creado
                    </span>
                </div>
            </div>
        </div>

        <div
            v-if="!showForm"
            class="group w-full bg-theme-card border-2 border-dashed rounded-xl p-8 text-center cursor-pointer border-stack-primary hover:border-stack-secondary transition"
            @click="showForm = true"
        >
            <p
                class="text-lg text-theme-primary group-hover:text-stack-secondary transition"
            >
                {{
                    onboardingDraft.products.length
                        ? "+ Agregar otro producto"
                        : "Crea tu primer producto"
                }}
            </p>
        </div>

        <div v-if="showForm" class="w-full space-y-6">
            <div
                class="bg-theme-card border border-theme-border rounded-xl p-6 space-y-5"
            >
                <div>
                    <label class="text-sm text-theme-muted block mb-1">
                        Nombre del producto *
                    </label>
                    <input
                        v-model="form.name"
                        placeholder="Nombre del producto"
                        class="outline-none text-base w-full border border-theme-border rounded-lg px-3 py-2 bg-theme-input text-theme-base"
                    />
                </div>

                <div>
                    <label class="text-sm text-theme-muted block mb-1">
                        Precio de venta *
                    </label>
                    <input-money-component
                        v-model="form.price"
                        enabled
                        border
                        label=""
                    />
                </div>

                <div>
                    <label class="text-sm text-theme-muted block mb-1">
                        SKU (opcional)
                    </label>
                    <input
                        v-model="form.sku"
                        placeholder="Código del producto"
                        class="outline-none text-sm w-full border border-theme-border rounded-lg px-3 py-2 bg-theme-input text-theme-base"
                    />
                </div>

                <div>
                    <p class="text-sm text-theme-muted mb-2">
                        Fotos (opcional)
                    </p>
                    <images-manage
                        id="onboarding-product-images"
                        v-model="form.images"
                        message="del producto"
                        default-size="squareImage"
                        :default-alt="'Imagen de ' + (form.name || 'producto')"
                        :default-filters="{ size: 'squareImage' }"
                        enabled
                        direct-upload
                    />
                </div>
            </div>

            <p v-if="formError" class="text-theme-danger text-sm">
                {{ formError }}
            </p>

            <div class="flex gap-3">
                <button
                    class="flex-1 py-3 rounded-full font-medium transition border border-theme-border text-theme-base hover:bg-theme-hover"
                    @click="showForm = false"
                >
                    Cancelar
                </button>
                <button
                    :disabled="isCreating"
                    class="flex-1 py-3 rounded-full font-medium transition bg-stack-primary text-white hover:opacity-90"
                    @click="createProduct"
                >
                    {{ isCreating ? "Creando..." : "Crear producto" }}
                </button>
            </div>
        </div>

        <p v-if="error" class="text-theme-danger text-sm mt-4">
            {{ error }}
        </p>
    </div>
</template>

<script>
import onboardingCompletedGuard from "@/mixins/onboardingCompletedGuard";
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapMutations } from "vuex";

import ImagesManage from "@/components/utils/ImagesManage.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

export default {
    name: "OnboardingProductsPage",

    components: { InputMoneyComponent, ImagesManage },

    mixins: [
        onboardingCompletedGuard,
        onboardingStepNavigation,
        planLimitError
    ],

    data() {
        return {
            stepKey: "products",
            showForm: false,
            isCreating: false,
            isLoading: false,
            formError: null,
            error: null,
            form: {
                name: "",
                price: 1000,
                sku: "",
                images: []
            }
        };
    },

    computed: {
        localStepperUi() {
            return this.onboardingDraft.products.length
                ? {
                      nextDisabled: false,
                      loading: this.isLoading,
                      nextLabel: "Terminé de agregar productos",
                      nextVariant: "primary"
                  }
                : {
                      nextDisabled: false,
                      loading: this.isLoading,
                      nextLabel: "Dejar para después",
                      nextVariant: "ghost"
                  };
        }
    },

    async mounted() {
        await this.ensureOnboardingStatus();
        if (this.redirectIfOnboardingCompleted()) return;
    },

    methods: {
        ...mapActions("products", ["recordProduct"]),
        ...mapActions("onboarding", ["confirmProducts"]),
        ...mapActions("auth", ["applyAllPermissions", "getProfile"]),
        ...mapMutations("onboarding", ["ADD_DRAFT_PRODUCT"]),
        resetForm() {
            this.form = {
                name: "",
                price: 1000,
                sku: "",
                images: []
            };
            this.formError = null;
        },

        async createProduct() {
            if (!this.form.name.trim()) {
                this.formError = "El nombre es obligatorio";
                return;
            }
            if (this.form.price <= 0) {
                this.formError = "El precio de venta debe ser mayor a $0";
                return;
            }

            this.isCreating = true;
            this.formError = null;
            try {
                const payload = {
                    name: this.form.name.trim(),
                    price: this.form.price,
                    type: "single",
                    active: true
                };
                if (this.form.sku) {
                    payload.sku = this.form.sku.trim();
                }
                if (this.form.images.length) {
                    payload.images = this.form.images.map((img) => ({
                        id: img.id,
                        sequence: img.sequence.sequence
                    }));
                }

                const result = await this.recordProduct(payload);
                this.ADD_DRAFT_PRODUCT({
                    id: result.id,
                    slug: result.slug,
                    name: payload.name,
                    active: true,
                    references: [
                        {
                            price: payload.price,
                            priceOffer: payload.price,
                            images: this.form.images.map((img) => ({
                                url: img.url
                            }))
                        }
                    ]
                });
                this.resetForm();
                this.showForm = false;
            } catch (err) {
                if (!this.openPlanUpgradeForLimit(err)) {
                    this.formError =
                        err.message || "Error al crear el producto";
                }
            } finally {
                this.isCreating = false;
            }
        },

        stepperNext() {
            if (this.readOnly) {
                this.goToNextReachable();
                return;
            }
            this.confirmStep();
        },

        async confirmStep() {
            if (this.isLoading) return;
            this.isLoading = true;
            this.error = null;
            try {
                const data = await this.confirmProducts();
                await this.handleFlowResponse(data.onboardingFlow);
                this.isLoading = false;
            } catch (err) {
                this.isLoading = false;
                this.error = err.message || "Error al confirmar";
            }
        },

        async handleFlowResponse(flow) {
            if (!flow) return;
            if (flow.status === "completed") {
                try {
                    const profile = await this.getProfile();
                    if (profile.permissions) {
                        this.applyAllPermissions({
                            permissions: profile.permissions
                        });
                    }
                } catch {
                    // Permissions applied on next login
                }
                if (!flow.nextStep) {
                    this.$router.push(this.completionRoute);
                    return;
                }
                const route = this.routeForStep(flow.nextStep);
                if (route) {
                    this.$router.push(route);
                    return;
                }
                this.$router.push("/app");
                return;
            }
            const route = this.routeForStep(flow.step);
            if (route) {
                this.$router.push(route);
            }
        }
    }
};
</script>
